import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type {
  ApprovalEvent,
  CreatePaymentRequest,
  PaymentRequestListRow,
  PaymentRequestStatus,
  PaymentRequestWithApprovals,
} from '@dcc-erp/shared';
import { CreatePaymentRequestSchema, fmt, fmtB, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { ApprovalTimeline } from '../components/approval/ApprovalTimeline';
import { AuditLog } from '../components/approval/AuditLog';
import { DecisionModal, type Decision } from '../components/approval/DecisionModal';
import { canActOnStep } from '../components/approval/gating';
import { Can } from '../components/Can';
import { BranchSelect, DepartmentSelect, DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { Drawer, PageHead } from '../components/layout';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import { useAuth } from '../lib/auth';
import { usePermissions } from '../lib/auth/usePermissions';
// PR query hooks come from their own file. Coordinator's post-merge pass
// folds the file into the queries barrel + adds payment-request.ts to the
// shared schemas barrel — until then we import the hooks via the explicit
// path while the type-only imports route through the canonical barrel.
import {
  useActiveCompany,
  useActiveCompanyId,
  useBranches,
  useCoa,
  useDepartments,
  useVendors,
} from '../lib/queries';
import {
  useApprovePaymentRequest,
  useCancelPaymentRequest,
  useCreatePaymentRequest,
  usePayPaymentRequest,
  usePaymentRequest,
  usePaymentRequestApprovalEvents,
  usePaymentRequests,
  useRejectPaymentRequest,
  useReturnPaymentRequest,
  useSubmitPaymentRequest,
} from '../lib/queries/payment-requests';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

/* ===== Types (UI view-model) ===== */
type PrStatusKey =
  | 'draft'
  | 'pending'
  | 'approved'
  | 'paid'
  | 'rejected'
  | 'returned'
  | 'cancelled';
type BadgeTone = 'neutral' | 'warning' | 'info' | 'positive' | 'negative';
type PrTab = 'draft' | 'approved' | 'paid' | 'rejected' | 'summary';

interface PrStatus {
  label: string;
  tone: string;
  badge: BadgeTone;
}

interface ExpCat {
  code: string;
  name: string;
  vatable: boolean;
  whtRate: number;
}

// View-model approval row — shaped to feed the shared ApprovalTimeline
// (stepNo/roleLabel/approvedBy/comment/approvedAt) AND the canActOnStep gate
// (stepNo/roleCode). Sourced ONLY from the server `approvals[]` (no client chain).
interface ApprovalStep {
  id: string;
  stepNo: number;
  roleCode: string;
  roleLabel: string;
  approvedAt: string | null;
  approvedBy: string | null;
  comment: string | null;
}

interface PrRow {
  id: string; // UUID from API
  voucherNo: string; // PR-YYMM-NNNNNN
  date: string;
  vendor: string;
  vendorId: string;
  exp: string; // categoryCode
  desc: string;
  branch: string;
  branchId: string | null;
  departmentId: string | null;
  subtotal: number;
  vat: number;
  wht: number;
  due: string;
  attach: number; // placeholder until attachments phase
  status: PrStatusKey;
  rawStatus: PaymentRequestStatus; // server status — fed to canActOnStep
  currentStep: number;
  // CURRENT step's required roleCode while PENDING (null otherwise). Present on
  // list rows so the row-level approve gate can check role match without the
  // detail chain.
  currentApproverRole: string | null;
  submittedBy: string | null;
  submittedAt: string | null;
  payDate?: string;
  paidBy?: string;
  jv1?: string;
  jv2?: string;
  rejectReason?: string;
  returnedReason?: string;
  vatAmount: number;
  whtAmount: number;
  total: number;
  netPay: number;
  // Live chain rows (detail call only — empty for list rows).
  approvalChain: ApprovalStep[];
  // Persisted chain length — present on list rows too, so the "ขั้น x/N" badge
  // renders without the full approval rows.
  chainLength: number;
}

/* ===== Constants (verbatim from prototype) ===== */
const PR_STATUS: Record<PrStatusKey, PrStatus> = {
  draft: { label: 'ฉบับร่าง', tone: '#52525b', badge: 'neutral' },
  pending: { label: 'รออนุมัติ', tone: '#b54708', badge: 'warning' },
  approved: { label: 'อนุมัติแล้ว · รอจ่าย', tone: '#1a56a6', badge: 'info' },
  paid: { label: 'จ่ายเงินแล้ว', tone: '#117a3d', badge: 'positive' },
  rejected: { label: 'ไม่อนุมัติ', tone: '#b42318', badge: 'negative' },
  returned: { label: 'ส่งกลับแก้ไข', tone: '#b54708', badge: 'warning' },
  cancelled: { label: 'ยกเลิก', tone: '#52525b', badge: 'neutral' },
};

// Phase 12: 6210-6217 = EXPENSE (S&A), 5110/5130 = COST (COGS). Both are
// Dr-natural P&L codes; the PR flow doesn't care about the AccountType
// distinction — it just posts Dr <code> for the GL leg.
const EXP_CATS: readonly ExpCat[] = [
  { code: '6210', name: 'ค่าโฆษณา', vatable: true, whtRate: 2.0 },
  { code: '6211', name: 'ค่าบริการทั่วไป', vatable: true, whtRate: 3.0 },
  { code: '6212', name: 'ค่าเช่า', vatable: true, whtRate: 5.0 },
  { code: '6213', name: 'ค่าขนส่ง', vatable: true, whtRate: 1.0 },
  { code: '6214', name: 'ค่าวัสดุสำนักงาน', vatable: true, whtRate: 0 },
  { code: '6215', name: 'ค่าบริการวิชาชีพ', vatable: true, whtRate: 3.0 },
  { code: '6216', name: 'ค่าโทรศัพท์ / Internet', vatable: true, whtRate: 0 },
  { code: '6217', name: 'ค่าซ่อมแซม', vatable: true, whtRate: 3.0 },
  { code: '5110', name: 'ซื้อวัตถุดิบ', vatable: true, whtRate: 0 },
  { code: '5130', name: 'ค่าโสหุ้ยการผลิต', vatable: true, whtRate: 0 },
];

// ===== Import / Export column schema =====
// Header keys for the spreadsheet — same keys the exporter writes so a file
// round-trips straight back through นำเข้า. The first block are the import
// columns; the export appends read-only/computed columns after them.
const PR_IMPORT_COLS = [
  'prDate',
  'dueDate',
  'vendorCode',
  'branchCode',
  'departmentCode',
  'categoryCode',
  'description',
  'amount',
  'vatRate',
  'whtRate',
  'paymentTerms',
  'paymentMethod',
] as const;

const PR_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'prDate',
    label: 'วันที่ในบิล',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-01',
  },
  { col: 'dueDate', label: 'กำหนดจ่าย', required: true, hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-31' },
  { col: 'vendorCode', label: 'รหัส Vendor', required: true, hint: 'รหัสผู้ขายที่มีอยู่จริง' },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'categoryCode',
    label: 'หมวดค่าใช้จ่าย',
    required: true,
    hint: '6210-6217 / 5110 / 5130 (GL code ที่กำหนดไว้)',
  },
  {
    col: 'description',
    label: 'รายละเอียด',
    required: true,
    hint: 'เช่น ค่าโฆษณา BTS งวด พ.ค. 2026',
  },
  { col: 'amount', label: 'มูลค่าก่อน VAT', required: true, hint: 'ตัวเลข > 0' },
  { col: 'vatRate', label: 'อัตรา VAT %', required: false, hint: 'เว้นว่าง = 7' },
  { col: 'whtRate', label: 'อัตรา WHT %', required: false, hint: 'เว้นว่าง = 0' },
  { col: 'paymentTerms', label: 'Payment Terms', required: false, hint: 'เช่น Net 30' },
  {
    col: 'paymentMethod',
    label: 'วิธีชำระ',
    required: false,
    hint: 'transfer / cheque / cash',
  },
];

const PR_TEMPLATE_EXAMPLES: (string | number)[][] = [
  [
    '2026-05-01',
    '2026-05-31',
    'V-0001',
    '',
    '',
    '6210',
    'ค่าโฆษณา BTS งวด พ.ค. 2026',
    100000,
    7,
    2,
    'Net 30',
    'transfer',
  ],
];

const _ATTACH_NAMES = [
  'ใบกำกับภาษี',
  'ใบเสนอราคา',
  'PO',
  'ภาพถ่ายงาน',
  'สัญญา',
  'Quote',
  'Approval Email',
];

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

const TODAY = new Date();

const API_TO_UI_STATUS: Record<PaymentRequestStatus, PrStatusKey> = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  PAID: 'paid',
  REJECTED: 'rejected',
  RETURNED: 'returned',
  CANCELLED: 'cancelled',
};

function getStatus(s: PrStatusKey): PrStatus {
  return PR_STATUS[s] ?? PR_STATUS.draft;
}

// Known codes carry their VAT/WHT defaults from EXP_CATS. Real COA codes that
// aren't in the legacy list degrade gracefully: name = the code itself, VAT-able
// with no auto-WHT (the user can still override WHT manually on the form).
function getCat(code: string): ExpCat {
  return EXP_CATS.find((x) => x.code === code) ?? { code, name: code, vatable: true, whtRate: 0 };
}

function prForm(rate: number): string {
  return rate > 0 ? '53' : '';
}

function dateOnly(d: string | Date): string {
  const s = typeof d === 'string' ? d : d.toISOString();
  return s.slice(0, 10);
}

/* ===== API → UI view-model adapter ===== */
function apiToVm(
  row: PaymentRequestListRow,
  approvals?: PaymentRequestWithApprovals['approvals'],
): PrRow {
  const subtotal = toNumber(row.amount);
  const vatAmount = toNumber(row.vatAmount);
  const whtAmount = toNumber(row.whtAmount);
  const total = toNumber(row.totalAmount);
  const netPay = toNumber(row.netAmount);
  const vatRate = toNumber(row.vatRate);
  const whtRate = toNumber(row.whtRate);
  // Chain is sourced ONLY from the server `approvals[]` (materialized on submit).
  // List rows carry no approval rows — only the detail call does — so the chain
  // is empty for list rows; the badge falls back to `approvalChainLength`.
  const chain: ApprovalStep[] = (approvals ?? []).map((a) => ({
    id: a.id,
    stepNo: a.stepNo,
    roleCode: a.roleCode,
    roleLabel: a.roleLabel,
    approvedAt: a.approvedAt ? dateOnly(a.approvedAt) : null,
    approvedBy: a.approvedBy ?? null,
    comment: a.comment ?? null,
  }));
  return {
    id: row.id,
    voucherNo: row.voucherNo,
    date: dateOnly(row.prDate),
    vendor: row.vendorName,
    vendorId: row.vendorId,
    exp: row.categoryCode,
    desc: row.description,
    branch: row.branchName ?? '—',
    branchId: row.branchId ?? null,
    departmentId: row.departmentId ?? null,
    subtotal,
    vat: vatRate,
    wht: whtRate,
    due: dateOnly(row.dueDate),
    attach: 0,
    status: API_TO_UI_STATUS[row.status],
    rawStatus: row.status,
    currentStep: row.currentStep,
    currentApproverRole: row.currentApproverRole ?? null,
    submittedBy: row.submittedBy ?? null,
    submittedAt: row.submittedAt ? dateOnly(row.submittedAt) : null,
    ...(row.paidAt ? { payDate: dateOnly(row.paidAt) } : {}),
    ...(row.paidBy ? { paidBy: row.paidBy } : {}),
    ...(row.jv1EntryId ? { jv1: row.jv1EntryId } : {}),
    ...(row.jv2EntryId ? { jv2: row.jv2EntryId } : {}),
    ...(row.rejectReason ? { rejectReason: row.rejectReason } : {}),
    ...(row.returnedReason ? { returnedReason: row.returnedReason } : {}),
    vatAmount,
    whtAmount,
    total,
    netPay,
    approvalChain: chain,
    chainLength: row.approvalChainLength,
  };
}

const TAB_MAP: Record<Exclude<PrTab, 'summary'>, PrStatusKey[]> = {
  // RETURNED groups with draft/pending — it's the editable/resubmittable bucket.
  draft: ['draft', 'pending', 'returned'],
  approved: ['approved'],
  paid: ['paid'],
  rejected: ['rejected', 'cancelled'],
};

/* ===== Route ===== */
export const Route = createFileRoute('/payreq')({
  component: PayreqPage,
});

function PayreqPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const { user } = useAuth();
  const { roles: viewerRoles } = usePermissions();
  // Server submittedBy stores `preferredUsername || email || entraOid` — match it
  // so the SoD check (submitter ≠ approver) lines up (same as journal.tsx).
  const viewerIdentity = user?.preferredUsername ?? user?.email ?? user?.entraOid ?? '';
  const [activeTab, setActiveTab] = useState<PrTab>('draft');
  const [q, setQ] = useState<string>('');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [showApprove, setShowApprove] = useState<PrRow | null>(null);
  const [showBatch, setShowBatch] = useState<boolean>(false);
  const [selPrId, setSelPrId] = useState<string | null>(null);

  // Unfiltered fetch — drives KPI totals + tab counts.
  const { data: rows, isPending, error } = usePaymentRequests(companyId, {});

  // Masters — code↔id maps for import resolution + export rendering.
  const { data: vendors = [] } = useVendors(companyId, true);
  const { data: branches = [] } = useBranches(companyId, true);
  const { data: departments = [] } = useDepartments(companyId, true);
  // Postable expense/cost accounts — used to validate categoryCode on import
  // against the real COA instead of the legacy hardcoded EXP_CATS list.
  const { data: coa = [] } = useCoa(companyId, true);
  const createPr = useCreatePaymentRequest(companyId);

  const submit = useSubmitPaymentRequest(companyId);
  const cancel = useCancelPaymentRequest(companyId);

  const vendorByCode = useMemo(
    () => new Map(vendors.map((v) => [v.code.toLowerCase(), v.id])),
    [vendors],
  );
  const vendorCodeById = useMemo(() => new Map(vendors.map((v) => [v.id, v.code])), [vendors]);
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const deptByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  // ===== Export =====
  const exportRowsList = rows ?? [];
  const EXPORT_COLS: ExportColumn<PaymentRequestListRow>[] = [
    { header: 'voucherNo', value: (r) => r.voucherNo, width: 16 },
    { header: 'prDate', value: (r) => String(r.prDate).slice(0, 10), width: 12 },
    { header: 'vendorCode', value: (r) => vendorCodeById.get(r.vendorId) ?? '', width: 12 },
    { header: 'description', value: (r) => r.description, width: 32 },
    { header: 'categoryCode', value: (r) => r.categoryCode, width: 12 },
    { header: 'dueDate', value: (r) => String(r.dueDate).slice(0, 10), width: 12 },
    { header: 'amount', value: (r) => Number(r.amount), width: 14 },
    { header: 'vatRate', value: (r) => Number(r.vatRate), width: 8 },
    { header: 'whtRate', value: (r) => Number(r.whtRate), width: 8 },
    // read-only / computed — ignored on re-import
    { header: 'whtAmount', value: (r) => Number(r.whtAmount), width: 12 },
    { header: 'netAmount', value: (r) => Number(r.netAmount), width: 14 },
    { header: 'totalAmount', value: (r) => Number(r.totalAmount), width: 14 },
    { header: 'status', value: (r) => r.status, width: 12 },
    { header: 'branchName', value: (r) => r.branchName ?? '', width: 16 },
  ];

  async function handleExport() {
    await exportRows(
      `ขออนุมัติจ่ายเงิน_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`,
      'ขออนุมัติจ่ายเงิน',
      EXPORT_COLS,
      exportRowsList,
    );
  }

  // ===== Import =====
  function parse(grid: string[][]): {
    units: ParsedUnit<CreatePaymentRequest>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'prDate',
      'dueDate',
      'vendorCode',
      'categoryCode',
      'description',
      'amount',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    // Accept any postable expense/cost code from the real COA. If the COA
    // hasn't loaded yet, fall back to the legacy EXP_CATS list so import still
    // validates rather than silently accepting everything.
    const postableCoaCodes = coa
      .filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl)
      .map((a) => a.code);
    const knownCats = new Set(
      postableCoaCodes.length > 0 ? postableCoaCodes : EXP_CATS.map((c) => c.code),
    );
    const units: ParsedUnit<CreatePaymentRequest>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const vendorCode = cell(cols, 'vendorCode');
      const vendorId = vendorCode ? vendorByCode.get(vendorCode.toLowerCase()) : undefined;
      if (!vendorId) {
        units.push({ rowNo, data: null, error: `ไม่พบ Vendor: ${vendorCode || '(ว่าง)'}` });
        continue;
      }

      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }

      const deptCode = cell(cols, 'departmentCode');
      const departmentId = deptCode ? deptByCode.get(deptCode.toLowerCase()) : undefined;
      if (deptCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${deptCode}` });
        continue;
      }

      const categoryCode = cell(cols, 'categoryCode');
      if (!knownCats.has(categoryCode)) {
        units.push({
          rowNo,
          data: null,
          error: `ไม่พบหมวดค่าใช้จ่าย: ${categoryCode || '(ว่าง)'}`,
        });
        continue;
      }

      const vatRaw = cell(cols, 'vatRate');
      const whtRaw = cell(cols, 'whtRate');
      const paymentTerms = cell(cols, 'paymentTerms');
      const paymentMethod = cell(cols, 'paymentMethod');
      const candidate = {
        prDate: cell(cols, 'prDate'),
        dueDate: cell(cols, 'dueDate'),
        vendorId,
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
        categoryCode,
        description: cell(cols, 'description'),
        amount: Number(cell(cols, 'amount')),
        ...(vatRaw ? { vatRate: Number(vatRaw) } : {}),
        ...(whtRaw ? { whtRate: Number(whtRaw) } : {}),
        ...(paymentTerms ? { paymentTerms } : {}),
        ...(paymentMethod ? { paymentMethod } : {}),
      };
      const p = CreatePaymentRequestSchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreatePaymentRequest>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createPr.mutateAsync(u.data);
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const PR_PREVIEW_COLS: PreviewColumn<CreatePaymentRequest>[] = [
    { header: 'วันที่', width: 90, render: (d) => d.prDate },
    {
      header: 'Vendor',
      width: 110,
      render: (d) => vendorCodeById.get(d.vendorId) ?? d.vendorId.slice(0, 8),
    },
    { header: 'หมวด', width: 70, render: (d) => d.categoryCode },
    { header: 'รายละเอียด', render: (d) => d.description },
    { header: 'มูลค่า', width: 110, render: (d) => fmt(d.amount) },
  ];

  const allVms = useMemo<PrRow[]>(() => (rows ?? []).map((r) => apiToVm(r)), [rows]);

  const filtered = useMemo<PrRow[]>(() => {
    return allVms.filter((p) => {
      if (activeTab !== 'summary') {
        const allow = TAB_MAP[activeTab];
        if (!allow.includes(p.status)) return false;
      }
      if (filterBranchId && p.branchId !== filterBranchId) return false;
      if (filterDepartmentId && p.departmentId !== filterDepartmentId) return false;
      const needle = q.toLowerCase();
      if (
        needle &&
        !(
          p.voucherNo.toLowerCase().includes(needle) ||
          p.vendor.toLowerCase().includes(needle) ||
          p.desc.toLowerCase().includes(needle)
        )
      )
        return false;
      return true;
    });
  }, [allVms, activeTab, q, filterBranchId, filterDepartmentId]);

  const totals = useMemo(() => {
    const pendingList = allVms.filter((p) => p.status === 'pending');
    const approvedList = allVms.filter((p) => p.status === 'approved');
    const paidList = allVms.filter((p) => p.status === 'paid');
    return {
      pendingCount: pendingList.length,
      pendingAmt: pendingList.reduce((s, p) => s + p.netPay, 0),
      approvedCount: approvedList.length,
      approvedAmt: approvedList.reduce((s, p) => s + p.netPay, 0),
      paidYTD: paidList.reduce((s, p) => s + p.netPay, 0),
      paidCount: paidList.length,
      rejected: allVms.filter((p) => p.status === 'rejected').length,
      overdue: allVms.filter((p) => p.status === 'approved' && new Date(p.due) < TODAY).length,
    };
  }, [allVms]);

  const tabs: readonly (readonly [PrTab, string])[] = [
    [
      'draft',
      `ฉบับร่าง / รออนุมัติ (${allVms.filter((p) => p.status === 'draft' || p.status === 'pending' || p.status === 'returned').length})`,
    ],
    ['approved', `อนุมัติแล้ว · รอจ่าย (${totals.approvedCount})`],
    ['paid', `จ่ายแล้ว (${totals.paidCount})`],
    ['rejected', `ตีกลับ / ยกเลิก (${totals.rejected})`],
    ['summary', 'สรุปและรายงาน'],
  ];

  const approvedRows = useMemo(() => allVms.filter((p) => p.status === 'approved'), [allVms]);

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'ขออนุมัติจ่ายเงิน']}
        title="ขออนุมัติจ่ายเงิน (Payment Request)"
        actions={
          <>
            <HelpButton guide="payreq" />
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={exportRowsList.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            {activeTab === 'approved' && totals.approvedCount > 0 && (
              <button className="btn" onClick={() => setShowBatch(true)}>
                <Icon name="check" size={14} /> Batch Payment ({totals.approvedCount})
              </button>
            )}
            <button className="btn primary" onClick={() => setShowNew(true)}>
              <Icon name="plus" size={14} /> สร้างใบขอจ่าย
            </button>
          </>
        }
      />

      {error && (
        <div
          style={{
            padding: 12,
            background: '#fce9e7',
            border: '1px solid #f4c1bd',
            borderRadius: 6,
            marginBottom: 12,
            color: 'var(--negative)',
          }}
        >
          โหลดข้อมูลล้มเหลว: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      )}

      {/* KPI */}
      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            รออนุมัติ
          </div>
          <div className="value tnum">
            {totals.pendingCount} <span className="unit">ใบ</span>
          </div>
          <div className="trend faint">รวม {fmt(totals.pendingAmt)} บาท</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            อนุมัติแล้ว · รอจ่าย
          </div>
          <div className="value tnum">
            {totals.approvedCount} <span className="unit">ใบ</span>
          </div>
          <div className="trend faint">
            รวม {fmt(totals.approvedAmt)} บาท ·{' '}
            {totals.overdue > 0 && (
              <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                {totals.overdue} ใบเลยกำหนด
              </span>
            )}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            จ่ายแล้วเดือนนี้
          </div>
          <div className="value tnum">
            {fmtB(totals.paidYTD)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> {totals.paidCount} ใบ · JV1+JV2 ลงครบ
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            ตีกลับ / ยกเลิก
          </div>
          <div className="value tnum">
            {totals.rejected} <span className="unit">ใบ</span>
          </div>
          <div className="trend down">ส่งกลับให้ผู้ขอแก้ไข</div>
        </div>
      </div>

      {/* Workflow banner */}
      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          วงจรเอกสาร — 5 ขั้นตอน (ระบบสร้าง JV ทั้ง 2 ใบให้อัตโนมัติ)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
          {(
            [
              { n: '1', t: 'สร้าง PR + แนบเอกสาร', c: '#52525b' },
              { n: '2', t: 'Approval Chain ตามวงเงิน', c: '#b54708' },
              { n: '3', t: 'JV1: Dr.ค่าใช้จ่าย / Cr.ค่าใช้จ่ายค้างจ่าย', c: '#1a56a6' },
              { n: '4', t: 'Cashier เลือกใบ → Batch Payment', c: '#7030A0' },
              { n: '5', t: 'JV2: Dr.ค่าใช้จ่ายค้างจ่าย / Cr.เงินสด', c: '#117a3d' },
            ] as const
          ).map((s) => (
            <div
              key={s.n}
              style={{
                display: 'flex',
                gap: 8,
                padding: '10px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: s.c,
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: 'var(--font-display)',
                  flexShrink: 0,
                }}
              >
                {s.n}
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.4, flex: 1 }}>{s.t}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div
            key={k}
            className={`pt ${activeTab === k ? 'active' : ''}`}
            onClick={() => setActiveTab(k)}
          >
            {l}
          </div>
        ))}
      </div>

      {isPending && <div style={{ padding: 20, color: 'var(--text-muted)' }}>กำลังโหลด…</div>}

      {!isPending && activeTab !== 'summary' && (
        <PRList
          rows={filtered}
          q={q}
          setQ={setQ}
          companyId={companyId}
          branchId={filterBranchId}
          departmentId={filterDepartmentId}
          onBranchChange={setFilterBranchId}
          onDepartmentChange={setFilterDepartmentId}
          tab={activeTab}
          viewerRoles={viewerRoles}
          viewerIdentity={viewerIdentity}
          onPick={(p) => setSelPrId(p.id)}
          onApprove={(p) => setShowApprove(p)}
          onSubmit={(p) => submit.mutate(p.id)}
          onCancel={(p) => cancel.mutate(p.id)}
          submitting={submit.isPending || cancel.isPending}
        />
      )}
      {!isPending && activeTab === 'summary' && <PRSummary rows={allVms} />}

      {showNew && companyId && (
        <PRNewModal companyId={companyId} onClose={() => setShowNew(false)} />
      )}
      {showImport && companyId && (
        <SpreadsheetImportModal<CreatePaymentRequest>
          title="นำเข้าใบขออนุมัติจ่าย"
          intro={
            <>
              เลือกไฟล์ Excel (.xlsx) หรือ CSV ตามหัวคอลัมน์ด้านล่าง — หนึ่งแถว = หนึ่งใบขอจ่าย
              (รายการเดียว). เอกสารหลายรายการให้สร้างผ่านปุ่ม “สร้างใบขอจ่าย”
            </>
          }
          fields={PR_IMPORT_FIELDS}
          templateColumns={[...PR_IMPORT_COLS]}
          templateExamples={PR_TEMPLATE_EXAMPLES}
          templateFilename="payment_request_template.xlsx"
          sheetName="ขออนุมัติจ่ายเงิน"
          parse={parse}
          previewColumns={PR_PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {showApprove && (
        <PRApprovalModal
          pr={showApprove}
          companyId={companyId}
          onClose={() => setShowApprove(null)}
        />
      )}
      {showBatch && (
        <PRBatchPaymentModal
          items={approvedRows}
          companyId={companyId}
          onClose={() => setShowBatch(false)}
        />
      )}
      {selPrId && companyId && (
        <PRDetailDrawer
          companyId={companyId}
          prId={selPrId}
          viewerRoles={viewerRoles}
          viewerIdentity={viewerIdentity}
          onClose={() => setSelPrId(null)}
          onApprove={(pr) => {
            setShowApprove(pr);
            setSelPrId(null);
          }}
        />
      )}
    </div>
  );
}

/* ===== List ===== */
interface PRListProps {
  rows: PrRow[];
  q: string;
  setQ: (v: string) => void;
  companyId: string | undefined;
  branchId: string;
  departmentId: string;
  onBranchChange: (id: string) => void;
  onDepartmentChange: (id: string) => void;
  tab: Exclude<PrTab, 'summary'>;
  /**
   * Role codes held by the viewer (from usePermissions). Combined with the row's
   * `currentApproverRole` + `viewerIdentity` to apply the FULL role + SoD gate on
   * the row decide button (the row-level equivalent of the drawer's canActOnStep).
   */
  viewerRoles: string[];
  /**
   * Viewer identity (preferredUsername || email || entraOid) — the SoD half of
   * the row decide gate (submitter can't act on own doc). The detail drawer runs
   * the same gate via canActOnStep over the loaded approval rows.
   */
  viewerIdentity: string;
  onPick: (p: PrRow) => void;
  onApprove: (p: PrRow) => void;
  onSubmit: (p: PrRow) => void;
  onCancel: (p: PrRow) => void;
  submitting: boolean;
}

function PRList({
  rows,
  q,
  setQ,
  companyId,
  branchId,
  departmentId,
  onBranchChange,
  onDepartmentChange,
  tab,
  viewerRoles,
  viewerIdentity,
  onPick,
  onApprove,
  onSubmit,
  onCancel,
  submitting,
}: PRListProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่ / Vendor / รายละเอียด"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <DimensionFilter
          companyId={companyId}
          branchId={branchId}
          departmentId={departmentId}
          onBranchChange={onBranchChange}
          onDepartmentChange={onDepartmentChange}
        />
        <input className="input w-sm" type="date" defaultValue="2026-05-01" />
        <span className="faint">ถึง</span>
        <input className="input w-sm" type="date" defaultValue="2026-05-31" />
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 540 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>{tab === 'approved' && <input type="checkbox" />}</th>
              <th style={{ width: 160 }}>เลขที่ PR</th>
              <th>Vendor</th>
              <th>รายละเอียด</th>
              <th>หมวด</th>
              <th>กำหนดจ่าย</th>
              <th className="num">มูลค่ารวม</th>
              <th className="num">WHT</th>
              <th className="num">จ่ายสุทธิ</th>
              <th>สถานะ</th>
              <th style={{ width: 140 }}>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const st = getStatus(p.status);
              const overdue = p.status === 'approved' && new Date(p.due) < TODAY;
              return (
                <tr key={p.id} onClick={() => onPick(p)} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    {tab === 'approved' && <input type="checkbox" />}
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{p.voucherNo}</a>
                    <div className="meta">{fmtDate(p.date)}</div>
                  </td>
                  <td>{p.vendor}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'normal' }}>{p.desc}</td>
                  <td className="code muted">
                    {p.exp} {getCat(p.exp).name}
                  </td>
                  <td className={overdue ? 'neg' : ''}>
                    {fmtDate(p.due)}
                    {overdue && <div className="meta neg">เลยกำหนด</div>}
                  </td>
                  <td className="num tnum">{fmt(p.total)}</td>
                  <td className="num tnum muted">{p.whtAmount ? '−' + fmt(p.whtAmount) : '—'}</td>
                  <td className="num tnum">
                    <b>{fmt(p.netPay)}</b>
                  </td>
                  <td>
                    {p.status === 'pending' ? (
                      <span className={`badge ${st.badge}`}>
                        <span className="bdot" />
                        รออนุมัติ ขั้น {p.currentStep || 1}/{p.chainLength}
                      </span>
                    ) : (
                      <span className={`badge ${st.badge}`}>
                        <span className="bdot" />
                        {st.label}
                      </span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Decide (open the modal). Full row-level gate — the row-level
                        equivalent of the drawer's canActOnStep: PENDING + the viewer
                        holds the current step's role + viewer ≠ submitter (SoD). The
                        detail drawer's canActOnStep stays the authoritative client
                        check; the server is authoritative regardless. */}
                    {p.status === 'pending' &&
                      !!p.currentApproverRole &&
                      viewerRoles.includes(p.currentApproverRole) &&
                      viewerIdentity !== p.submittedBy && (
                        <Can permission="payreq.approve">
                          <button
                            className="btn sm primary"
                            style={{ height: 28, fontSize: 14 }}
                            onClick={() => onApprove(p)}
                          >
                            พิจารณา →
                          </button>
                        </Can>
                      )}
                    {/* Submit (DRAFT) + resubmit (RETURNED) — both go through submit. */}
                    {(p.status === 'draft' || p.status === 'returned') && (
                      <Can permission="payreq.submit">
                        <button
                          className="btn sm"
                          style={{ height: 28, fontSize: 14 }}
                          disabled={submitting}
                          onClick={() => onSubmit(p)}
                        >
                          {p.status === 'returned' ? 'ส่งใหม่' : 'ส่งอนุมัติ'}
                        </button>
                      </Can>
                    )}
                    {/* Cancel — DRAFT or RETURNED (rejected is terminal). */}
                    {(p.status === 'draft' || p.status === 'returned') && (
                      <Can permission="payreq.cancel">
                        <button
                          className="btn sm ghost"
                          style={{ height: 28, fontSize: 14, marginLeft: 4 }}
                          disabled={submitting}
                          onClick={() => onCancel(p)}
                        >
                          ยกเลิก
                        </button>
                      </Can>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={11}
                  style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}
                >
                  ยังไม่มีข้อมูล
                </td>
              </tr>
            )}
            {rows.length > 0 && (
              <tr className="total">
                <td colSpan={6}>รวม {rows.length} ใบ</td>
                <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.total, 0))}</td>
                <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.whtAmount, 0))}</td>
                <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.netPay, 0))}</td>
                <td colSpan={2} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== Summary ===== */
interface ByCat extends ExpCat {
  count: number;
  total: number;
}

function PRSummary({ rows }: { rows: PrRow[] }) {
  const byCat: Record<string, ByCat> = {};
  rows.forEach((p) => {
    const cat = getCat(p.exp);
    const bucket = byCat[p.exp] ?? { ...cat, count: 0, total: 0 };
    bucket.count += 1;
    bucket.total += p.netPay;
    byCat[p.exp] = bucket;
  });
  const maxCat = Math.max(...Object.values(byCat).map((c) => c.total), 1);

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>สรุปตามหมวดค่าใช้จ่าย</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>หมวด</th>
                <th className="num">ใบ</th>
                <th className="num">มูลค่ารวม</th>
                <th style={{ width: 180 }}>สัดส่วน</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(byCat)
                .sort((a, b) => b.total - a.total)
                .map((c) => (
                  <tr key={c.code}>
                    <td>
                      <span className="code muted">{c.code}</span> {c.name}
                    </td>
                    <td className="num">{c.count}</td>
                    <td className="num tnum">
                      <b>{fmt(c.total)}</b>
                    </td>
                    <td>
                      <div
                        style={{
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${(c.total / maxCat) * 100}%`,
                            height: '100%',
                            background: 'var(--brand)',
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              {Object.keys(byCat).length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}
                  >
                    ยังไม่มีข้อมูล
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>สถิติทั่วไป</h3>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            <div>
              จำนวนทั้งหมด: <b>{rows.length}</b> ใบ
            </div>
            <div>
              มูลค่ารวม: <b className="tnum">{fmt(rows.reduce((s, p) => s + p.netPay, 0))}</b> บาท
            </div>
            <div>
              WHT รวม: <b className="tnum">{fmt(rows.reduce((s, p) => s + p.whtAmount, 0))}</b> บาท
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Detail Drawer (loads detail by id) ===== */
function PRDetailDrawer({
  companyId,
  prId,
  viewerRoles,
  viewerIdentity,
  onClose,
  onApprove,
}: {
  companyId: string;
  prId: string;
  viewerRoles: string[];
  viewerIdentity: string;
  onClose: () => void;
  onApprove: (pr: PrRow) => void;
}) {
  const { data, isPending } = usePaymentRequest(companyId, prId);
  // Append-only audit history (Component 7) — the immutable event stream.
  const { data: auditEvents = [] } = usePaymentRequestApprovalEvents(companyId, prId);
  const vm = useMemo<PrRow | null>(() => {
    if (!data) return null;
    return apiToVm(data, data.approvals);
  }, [data]);

  return (
    <Drawer onClose={onClose}>
      {(isPending || !vm) && <div style={{ padding: 22 }}>กำลังโหลด…</div>}
      {vm && (
        <PRDetailContent
          pr={vm}
          auditEvents={auditEvents}
          viewerRoles={viewerRoles}
          viewerIdentity={viewerIdentity}
          onClose={onClose}
          onApprove={() => onApprove(vm)}
        />
      )}
    </Drawer>
  );
}

function PRDetailContent({
  pr,
  auditEvents,
  viewerRoles,
  viewerIdentity,
  onClose,
  onApprove,
}: {
  pr: PrRow;
  auditEvents: ApprovalEvent[];
  viewerRoles: string[];
  viewerIdentity: string;
  onClose: () => void;
  onApprove: () => void;
}) {
  const cat = getCat(pr.exp);
  const st = getStatus(pr.status);
  // Role + SoD gate for the decide button (mirrors the engine's assertApprover).
  const canAct = canActOnStep(
    {
      status: pr.rawStatus,
      currentStep: pr.currentStep,
      approvals: pr.approvalChain,
      submittedBy: pr.submittedBy,
    },
    viewerRoles,
    viewerIdentity,
  );
  const currentApproval = pr.approvalChain.find((a) => a.stepNo === pr.currentStep);
  return (
    <>
      <div
        style={{
          padding: '18px 22px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            {pr.voucherNo}
          </h3>
          <div className="meta" style={{ marginTop: 2 }}>
            {pr.vendor} · {fmtDate(pr.date)} ·{' '}
            <span className={`badge ${st.badge}`}>
              <span className="bdot" />
              {st.label}
            </span>
          </div>
        </div>
        <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
          <Icon name="x" size={18} />
        </button>
      </div>

      <div
        style={{
          padding: '22px',
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div>
          <div className="section-title" style={{ margin: '0 0 10px' }}>
            ข้อมูลใบขอจ่าย
          </div>
          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
              fontSize: 14,
            }}
          >
            <div>
              <div className="meta">Vendor</div>
              <div style={{ fontWeight: 500 }}>{pr.vendor}</div>
            </div>
            <div>
              <div className="meta">หมวดค่าใช้จ่าย</div>
              <div className="code">
                {cat.code} <span style={{ fontWeight: 500 }}>{cat.name}</span>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="meta">รายละเอียด</div>
              <div style={{ fontWeight: 500, marginTop: 2 }}>{pr.desc}</div>
            </div>
            <div>
              <div className="meta">สาขา / Cost Center</div>
              <div style={{ fontWeight: 500 }}>{pr.branch}</div>
            </div>
            <div>
              <div className="meta">กำหนดจ่าย</div>
              <div style={{ fontWeight: 500 }}>{fmtDate(pr.due)}</div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-title" style={{ margin: '0 0 10px' }}>
            จำนวนเงิน
          </div>
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
          >
            <tbody>
              <tr>
                <td>มูลค่าสินค้า/บริการ (Sub-total)</td>
                <td className="num tnum">{fmt(pr.subtotal)}</td>
              </tr>
              {pr.vatAmount > 0 && (
                <tr>
                  <td>+ ภาษีมูลค่าเพิ่ม (VAT {pr.vat}%)</td>
                  <td className="num tnum">{fmt(pr.vatAmount)}</td>
                </tr>
              )}
              <tr className="subtotal">
                <td>รวม (ก่อนหัก WHT)</td>
                <td className="num tnum">{fmt(pr.total)}</td>
              </tr>
              {pr.whtAmount > 0 && (
                <tr>
                  <td>
                    − ภาษีหัก ณ ที่จ่าย ({pr.wht}% จาก {fmt(pr.subtotal)})
                  </td>
                  <td className="num tnum">({fmt(pr.whtAmount)})</td>
                </tr>
              )}
              <tr className="total">
                <td>จ่ายสุทธิ</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(pr.netPay)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* PENDING read-only affordance for non-actors (wrong role / submitter). */}
        {pr.status === 'pending' && !canAct && currentApproval && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fef3e8',
              border: '1px solid #f0dab0',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            รออนุมัติจาก <strong>{currentApproval.roleLabel}</strong> · ขั้น {pr.currentStep}/
            {pr.approvalChain.length}
          </div>
        )}

        {/* RETURNED reason banner — shown so the requester knows what to fix. */}
        {pr.status === 'returned' && pr.returnedReason && (
          <div
            style={{
              padding: '14px 16px',
              background: '#fef3e8',
              borderRadius: 8,
              border: '1px solid #f0dab0',
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <b style={{ color: '#b54708' }}>เหตุผลที่ส่งกลับแก้ไข:</b> {pr.returnedReason}
          </div>
        )}

        <div>
          <div className="section-title" style={{ margin: '0 0 10px' }}>
            สายอนุมัติ (Approval Chain)
          </div>
          <ApprovalTimeline
            approvals={pr.approvalChain}
            isActive={pr.status === 'pending'}
            currentStep={pr.currentStep}
            emptyText="ยังไม่ส่งอนุมัติ"
            leadLine={
              pr.submittedBy ? (
                <div className="meta">
                  ผู้จัดทำ / ส่งอนุมัติ {pr.submittedBy}
                  {pr.submittedAt ? ` · ${fmtDate(pr.submittedAt)}` : ''}
                </div>
              ) : undefined
            }
            trailLine={
              pr.status === 'paid' && pr.paidBy ? (
                <div className="meta">
                  จ่ายโดย {pr.paidBy}
                  {pr.payDate ? ` · ${fmtDate(pr.payDate)}` : ''}
                </div>
              ) : undefined
            }
          />
        </div>

        {/* Append-only audit history (Component 7). */}
        <AuditLog events={auditEvents} />

        {pr.status === 'paid' && (
          <div>
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              การลงบัญชี
            </div>
            <div
              style={{
                padding: '14px 16px',
                background: '#e7f4ec',
                borderRadius: 8,
                border: '1px solid #bbdebf',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 14,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  <b>JV1 ตั้งค่าใช้จ่ายค้างจ่าย:</b>
                </span>{' '}
                <span className="code">{pr.jv1?.slice(0, 8)}…</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  <b>JV2 จ่ายเงิน:</b>
                </span>{' '}
                <span className="code">{pr.jv2?.slice(0, 8)}…</span>
              </div>
              {pr.payDate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <b>วันที่จ่าย:</b>
                  </span>
                  <span>{fmtDate(pr.payDate)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {pr.status === 'rejected' && pr.rejectReason && (
          <div
            style={{
              padding: '14px 16px',
              background: '#fce9e7',
              borderRadius: 8,
              border: '1px solid #f4c1bd',
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <b style={{ color: 'var(--negative)' }}>เหตุผลที่ไม่อนุมัติ:</b> {pr.rejectReason}
          </div>
        )}
      </div>

      <div
        style={{
          padding: '14px 22px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          background: '#fafafa',
        }}
      >
        <button className="btn ghost" onClick={onClose}>
          ปิด
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {pr.status === 'pending' && canAct && (
            <Can permission="payreq.approve">
              <button className="btn primary" onClick={onApprove}>
                <Icon name="check" size={14} /> พิจารณา
              </button>
            </Can>
          )}
        </div>
      </div>
    </>
  );
}

/* ===== New PR Modal ===== */
function PRNewModal({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const { data: vendors } = useVendors(companyId, true);
  const create = useCreatePaymentRequest(companyId);

  // Postable expense/cost accounts from the real COA (control/header accounts
  // can't be posted). These are the GL codes a PR can be charged to — the
  // legacy EXP_CATS list now only seeds VAT/WHT defaults for known codes.
  const { data: coa = [] } = useCoa(companyId, true);
  const expenseAccounts = useMemo(
    () => coa.filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl),
    [coa],
  );

  const [vendorId, setVendorId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [exp, setExp] = useState<string>('');
  const [subtotal, setSubtotal] = useState<number>(100_000);
  const [vatPct, setVatPct] = useState<number>(7);
  const [description, setDescription] = useState<string>('');
  const [prDate, setPrDate] = useState<string>(() => dateOnly(new Date()));
  const [dueDate, setDueDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return dateOnly(d);
  });
  const [paymentTerms, setPaymentTerms] = useState<string>('Net 30');
  const [paymentMethod, setPaymentMethod] = useState<string>('transfer');

  const cat = getCat(exp);
  const effectiveVat = cat.vatable ? Math.max(vatPct, 0) : 0;
  const vatAmt = effectiveVat > 0 ? Math.round((subtotal * effectiveVat) / 100) : 0;
  const whtAmt = cat.whtRate > 0 ? Math.round((subtotal * cat.whtRate) / 100) : 0;
  const total = subtotal + vatAmt;
  const netPay = total - whtAmt;

  const canSubmit =
    !!vendorId && !!exp && subtotal > 0 && description.length > 0 && !create.isPending;

  const onSave = () => {
    if (!canSubmit) return;
    const payload: CreatePaymentRequest = {
      prDate,
      dueDate,
      vendorId,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      categoryCode: exp,
      description,
      amount: subtotal,
      vatRate: effectiveVat,
      whtRate: cat.whtRate,
      paymentTerms,
      paymentMethod,
    };
    create.mutate(payload, { onSuccess: () => onClose() });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 1080,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              สร้างใบขออนุมัติจ่ายเงิน
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะ generate เลขที่ <b style={{ color: '#3f3f46' }}>PR-YYMM-NNNNNN</b> ให้อัตโนมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '22px',
            overflow: 'auto',
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 20,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="section-title" style={{ margin: 0 }}>
              ข้อมูลพื้นฐาน
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  Vendor / ผู้ขาย
                </label>
                <select
                  className="select"
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option value="">เลือก Vendor</option>
                  {vendors?.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.code} — {v.nameTh}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  วันที่ในบิล
                </label>
                <input
                  className="input"
                  type="date"
                  value={prDate}
                  onChange={(e) => setPrDate(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  สาขา
                </label>
                <BranchSelect
                  companyId={companyId}
                  value={branchId}
                  onChange={setBranchId}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  หน่วยงาน
                </label>
                <DepartmentSelect
                  companyId={companyId}
                  value={departmentId}
                  onChange={setDepartmentId}
                  branchId={branchId}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>

            <div>
              <label
                className="meta"
                style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
              >
                หมวดค่าใช้จ่าย (GL Code)
              </label>
              <select
                className="select"
                value={exp}
                onChange={(e) => setExp(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— เลือกบัญชีค่าใช้จ่าย —</option>
                {expenseAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} — {a.nameTh}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className="meta"
                style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
              >
                รายละเอียด
              </label>
              <textarea
                className="input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="เช่น ค่าโฆษณา BTS งวด พ.ค. 2026"
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 14,
                  minHeight: 60,
                  resize: 'vertical',
                }}
              />
            </div>

            <div
              style={{
                padding: '14px 16px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid var(--border)',
              }}
            >
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                จำนวนเงิน · คำนวณ VAT/WHT อัตโนมัติ
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div>
                  <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
                    มูลค่าก่อน VAT (Sub-total)
                  </label>
                  <input
                    className="input tnum"
                    type="number"
                    value={subtotal}
                    onChange={(e) => setSubtotal(parseFloat(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      height: 38,
                      fontSize: 15,
                      textAlign: 'right',
                      fontWeight: 600,
                    }}
                  />
                </div>
                <div>
                  <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
                    VAT
                  </label>
                  <select
                    className="select"
                    value={vatPct}
                    onChange={(e) => setVatPct(parseInt(e.target.value, 10))}
                    style={{ width: '100%', height: 38, fontSize: 14 }}
                  >
                    <option value="7">VAT 7%</option>
                    <option value="0">VAT 0% (Export)</option>
                  </select>
                </div>
              </div>
              <table
                className="tbl"
                style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
              >
                <tbody>
                  <tr>
                    <td>Sub-total</td>
                    <td className="num tnum">{fmt(subtotal)}</td>
                  </tr>
                  {vatAmt > 0 && (
                    <tr>
                      <td>+ VAT {effectiveVat}%</td>
                      <td className="num tnum">{fmt(vatAmt)}</td>
                    </tr>
                  )}
                  <tr className="subtotal">
                    <td>มูลค่ารวม</td>
                    <td className="num tnum">{fmt(total)}</td>
                  </tr>
                  {whtAmt > 0 && (
                    <tr>
                      <td>− WHT {cat.whtRate}% (จากมูลค่าก่อน VAT)</td>
                      <td className="num tnum">({fmt(whtAmt)})</td>
                    </tr>
                  )}
                  <tr className="total">
                    <td>จ่ายสุทธิให้ Vendor</td>
                    <td className="num tnum" style={{ color: 'var(--brand)' }}>
                      {fmt(netPay)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  Payment Terms
                </label>
                <select
                  className="select"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option>Net 30</option>
                  <option>Net 14</option>
                  <option>Net 45</option>
                  <option>Net 60</option>
                  <option>Net 90</option>
                </select>
              </div>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  วิธีชำระ
                </label>
                <select
                  className="select"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option value="transfer">โอนเงินผ่านธนาคาร</option>
                  <option value="cheque">เช็ค</option>
                  <option value="cash">เงินสด</option>
                </select>
              </div>
              <div>
                <label
                  className="meta"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
                >
                  กำหนดจ่าย
                </label>
                <input
                  className="input"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>
          </div>

          {/* Right column — Preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="section-title" style={{ margin: 0 }}>
              Preview การลงบัญชี (Auto)
            </div>

            {/* The approval chain is resolved by the server matrix (by docType +
                amount + branch/cost-center) at submit time — not previewed at
                create. It appears in the detail drawer once the PR is submitted. */}
            <div
              style={{
                padding: '14px 16px',
                background: '#e8eff8',
                borderRadius: 8,
                border: '1px solid #c1d1e8',
                fontSize: 13.5,
                lineHeight: 1.5,
              }}
            >
              <b>สายอนุมัติ</b> จะถูกกำหนดโดยระบบ (ตามประเภทเอกสาร · วงเงิน · สาขา/หน่วยงาน) เมื่อกด
              “ส่งอนุมัติ” และจะแสดงในหน้ารายละเอียดของใบขอจ่าย
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div
                style={{
                  padding: '10px 14px',
                  background: '#fafafa',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <b style={{ fontSize: 14 }}>JV1 — ตั้งค่าใช้จ่ายค้างจ่าย</b>
                <div className="meta">ระบบสร้างเมื่ออนุมัติครบทุกขั้น</div>
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>บัญชี</th>
                    <th>ชื่อ</th>
                    <th className="num">Dr</th>
                    <th className="num">Cr</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="code">{cat.code}</td>
                    <td>{cat.name}</td>
                    <td className="num tnum">{fmt(subtotal)}</td>
                    <td className="num">—</td>
                  </tr>
                  {vatAmt > 0 && (
                    <tr>
                      <td className="code">10190012</td>
                      <td>ภาษีซื้อ (Input VAT)</td>
                      <td className="num tnum">{fmt(vatAmt)}</td>
                      <td className="num">—</td>
                    </tr>
                  )}
                  <tr>
                    <td className="code">20150001</td>
                    <td>เจ้าหนี้การค้า</td>
                    <td className="num">—</td>
                    <td className="num tnum">{fmt(total - whtAmt)}</td>
                  </tr>
                  {whtAmt > 0 && (
                    <tr>
                      <td className="code">20170011</td>
                      <td>WHT ค้างนำส่ง</td>
                      <td className="num">—</td>
                      <td className="num tnum">{fmt(whtAmt)}</td>
                    </tr>
                  )}
                  <tr className="total">
                    <td colSpan={2}>รวม</td>
                    <td className="num tnum">{fmt(total)}</td>
                    <td className="num tnum">{fmt(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div
                style={{
                  padding: '10px 14px',
                  background: '#fafafa',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <b style={{ fontSize: 14 }}>JV2 — บันทึกการจ่ายเงิน</b>
                <div className="meta">ระบบสร้างเมื่อ Cashier ทำ Payment</div>
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>บัญชี</th>
                    <th>ชื่อ</th>
                    <th className="num">Dr</th>
                    <th className="num">Cr</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="code">20150001</td>
                    <td>เจ้าหนี้การค้า (ปิดยอด)</td>
                    <td className="num tnum">{fmt(total - whtAmt)}</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td className="code">—</td>
                    <td>เงินฝากธนาคาร (บัญชีหลักของบริษัท)</td>
                    <td className="num">—</td>
                    <td className="num tnum">{fmt(total - whtAmt)}</td>
                  </tr>
                  <tr className="total">
                    <td colSpan={2}>รวม</td>
                    <td className="num tnum">{fmt(total - whtAmt)}</td>
                    <td className="num tnum">{fmt(total - whtAmt)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {whtAmt > 0 && (
              <div
                style={{
                  padding: '12px 14px',
                  background: '#efe6f4',
                  borderRadius: 6,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}
              >
                ระบบจะออก <b>หนังสือรับรองหัก ณ ที่จ่าย</b> อัตโนมัติเมื่อจ่ายเงิน · เข้า ภ.ง.ด.{' '}
                {prForm(cat.whtRate)} ของเดือน
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          {create.isError && (
            <span style={{ color: 'var(--negative)', fontSize: 13, alignSelf: 'center' }}>
              {create.error instanceof Error ? create.error.message : 'create failed'}
            </span>
          )}
          <button className="btn primary" disabled={!canSubmit} onClick={onSave}>
            <Icon name="check" size={14} /> บันทึก (สร้างฉบับร่าง)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Approval Modal — thin wrapper over the shared 3-action DecisionModal ===== */
interface PRApprovalModalProps {
  pr: PrRow;
  companyId: string | undefined;
  onClose: () => void;
}

function PRApprovalModal({ pr, companyId, onClose }: PRApprovalModalProps) {
  const approve = useApprovePaymentRequest(companyId);
  const returnForRevision = useReturnPaymentRequest(companyId);
  const reject = useRejectPaymentRequest(companyId);
  const [error, setError] = useState<string | null>(null);
  const busy = approve.isPending || returnForRevision.isPending || reject.isPending;
  const step = pr.currentStep || 1;

  // approve → /approve {step,comment?}; return → /return {step,reason};
  // reject (terminal) → /reject {step,reason}.
  async function handleConfirm(d: Decision) {
    setError(null);
    try {
      if (d.action === 'approve') {
        await approve.mutateAsync({
          id: pr.id,
          step: d.step,
          ...(d.comment ? { comment: d.comment } : {}),
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnForRevision.mutateAsync({
          id: pr.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await reject.mutateAsync({
          id: pr.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <DecisionModal
      voucherNo={pr.voucherNo}
      step={step}
      busy={busy}
      error={error}
      onConfirm={(d) => {
        void handleConfirm(d);
      }}
      onClose={onClose}
    />
  );
}

/* ===== Batch Payment Modal — sequential per-row pay() calls ===== */
interface PRBatchPaymentModalProps {
  items: PrRow[];
  companyId: string | undefined;
  onClose: () => void;
}

function PRBatchPaymentModal({ items, companyId, onClose }: PRBatchPaymentModalProps) {
  // Empty = let the backend resolve BANK_PRIMARY (the company's configured default
  // cash account, set in /settings/account-mapping). Optionally override per-run.
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [paidAt, setPaidAt] = useState<string>(dateOnly(new Date()));
  const pay = usePayPaymentRequest(companyId);

  const total = items.reduce((s, p) => s + p.netPay, 0);
  const whtTotal = items.reduce((s, p) => s + p.whtAmount, 0);

  const onConfirm = async () => {
    // Sequential — keeps idempotency keys + JV ordering deterministic. Phase 4b
    // will bring a single batch endpoint that bundles everything in one JV.
    for (const p of items) {
      await pay.mutateAsync({
        id: p.id,
        cashAccountCode: cashAccountCode.trim() || undefined,
        paidAt,
      });
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 1100,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              Batch Payment · จ่ายเงินรวบทีเดียว
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {items.length} ใบ PR · ยอดสุทธิ <b style={{ color: 'var(--brand)' }}>{fmt(total)}</b>{' '}
              บาท
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '22px',
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 14,
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
            }}
          >
            <div>
              <div className="meta">จำนวนใบ PR</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{items.length}</div>
            </div>
            <div>
              <div className="meta">ยอดรวม (ก่อนหัก WHT)</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }} className="tnum">
                {fmt(total + whtTotal)}
              </div>
            </div>
            <div>
              <div className="meta">หัก WHT รวม</div>
              <div
                style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: '#7030A0' }}
                className="tnum"
              >
                {fmt(whtTotal)}
              </div>
            </div>
            <div>
              <div className="meta">โอนสุทธิ</div>
              <div
                style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: 'var(--brand)' }}
                className="tnum"
              >
                {fmt(total)}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                className="meta"
                style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
              >
                วันที่จ่าย
              </label>
              <input
                className="input"
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                className="meta"
                style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}
              >
                COA บัญชีต้นทาง — เว้นว่างได้
              </label>
              <input
                className="input code"
                value={cashAccountCode}
                onChange={(e) => setCashAccountCode(e.target.value)}
                placeholder="เว้นว่าง = บัญชีธนาคารหลัก (ตามตั้งค่าบัญชีอ้างอิง)"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>เลขที่ PR</th>
                  <th>Vendor</th>
                  <th>กำหนดจ่าย</th>
                  <th className="num">มูลค่ารวม</th>
                  <th className="num">WHT</th>
                  <th className="num">โอนสุทธิ</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td className="code">{p.voucherNo}</td>
                    <td>{p.vendor}</td>
                    <td>{fmtDate(p.due)}</td>
                    <td className="num tnum">{fmt(p.total)}</td>
                    <td className="num tnum muted">{p.whtAmount ? '−' + fmt(p.whtAmount) : '—'}</td>
                    <td className="num tnum">
                      <b>{fmt(p.netPay)}</b>
                    </td>
                  </tr>
                ))}
                <tr className="total">
                  <td colSpan={3}>รวม {items.length} ใบ</td>
                  <td className="num tnum">{fmt(items.reduce((s, p) => s + p.total, 0))}</td>
                  <td className="num tnum">{fmt(whtTotal)}</td>
                  <td className="num tnum">{fmt(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={pay.isPending || items.length === 0}
            onClick={() => {
              void onConfirm();
            }}
          >
            <Icon name="check" size={14} /> ยืนยัน · จ่ายเงิน {items.length} ใบ
          </button>
        </div>
      </div>
    </div>
  );
}
